import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./useAuth";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import { getModeFromPath } from "../core/utils/getMode";
import GlobalLoadingPage from "../core/components/GlobalLoadingPage";
import useMinimumLoading from "../core/hooks/useMinimumLoading";
import { fetchOrganizationAccess } from "../core/settings/organizationAccessService";
import workspaceLogo from "../assets/logos/workplace_logo.png";

const PROFILE_ICON = "/app-icons/profile-home-icon.png";
const PROFILE_COLOR = "#2563eb";

function getCachedEduOrganization() {
  if (typeof window === "undefined") return null;

  try {
    const raw =
      window.localStorage.getItem("oikos.organization.edu") ||
      window.localStorage.getItem("oikos.organization.campus");
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

function getMetadataObject(value = null) {
  return value && typeof value === "object" ? value : {};
}

function getProfileImageUrl({ profile = null, user = null } = {}) {
  const profileMetadata = getMetadataObject(profile?.metadata);
  const profileExtraData = getMetadataObject(profileMetadata.extra_data);
  const userMetadata = getMetadataObject(user?.user_metadata);
  const userExtraData = getMetadataObject(userMetadata.extra_data);
  const identityData = getMetadataObject(user?.identities?.[0]?.identity_data);

  return (
    profileMetadata.avatar_url ||
    profileMetadata.avatarUrl ||
    profileMetadata.picture ||
    profileMetadata.profile_image_url ||
    profileMetadata.profileImageUrl ||
    profileExtraData.avatar_url ||
    profileExtraData.avatarUrl ||
    profileExtraData.picture ||
    profileExtraData.profile_image_url ||
    profileExtraData.profileImageUrl ||
    userMetadata.avatar_url ||
    userMetadata.avatarUrl ||
    userMetadata.picture ||
    userMetadata.profile_image_url ||
    userMetadata.profileImageUrl ||
    userExtraData.avatar_url ||
    userExtraData.avatarUrl ||
    userExtraData.picture ||
    userExtraData.profile_image_url ||
    userExtraData.profileImageUrl ||
    identityData.avatar_url ||
    identityData.avatarUrl ||
    identityData.picture ||
    ""
  );
}

function resolveAccessScope(path) {
  const normalizedPath = String(path || "").toLowerCase();
  const detectedMode = getModeFromPath(
    path,
    window.location.hostname
  );

  let platform = "display";
  let mode = detectedMode;

  if (normalizedPath.startsWith("/campus/teacher")) {
    platform = "campus";
    mode = "teacher_portal";
  } else if (normalizedPath === "/edu") {
    platform = "edu";
    mode = "default";
  } else if (normalizedPath.startsWith("/classroom/admin")) {
    platform = "edu";
    mode = "default";
  } else if (normalizedPath.startsWith("/devices/teacher") || normalizedPath.startsWith("/edu/teacher")) {
    platform = "edu";
    mode = "teacher_portal";
  } else if (normalizedPath.startsWith("/devices/admin") || normalizedPath.startsWith("/edu/admin") || normalizedPath.startsWith("/workspace/admin")) {
    platform = "edu";
    mode = "default";
  } else if (
    ["admin", "campus", "pages"].includes(
      detectedMode
    )
  ) {
    platform = detectedMode;
    mode = "default";
  }

  return {
    detectedMode,
    mode,
    platform,
    key: `${platform}:${mode}`,
  };
}

export default function RequireAuth({ children }) {
  const { user, profile, profileReady, loading } = useAuth();
  const location = useLocation();

  const [hasAccess, setHasAccess] = useState(null);
  const [checked, setChecked] = useState(false);
  const [checkedScopeKey, setCheckedScopeKey] = useState("");

  const path = location.pathname;
  const normalizedPath = String(path || "").toLowerCase();
  const userId = user?.id || "";
  const accessScope = useMemo(() => resolveAccessScope(path), [path]);
  const accessCheckKey = `${userId}:${accessScope.key}`;
  const isEduLauncherRoute = normalizedPath === "/edu";
  const isWorkspaceRoute = path.startsWith("/workspace");
  const isProfileRoute = path.startsWith("/profile");
  const profileImageUrl = getProfileImageUrl({ profile, user });
  const loadingModeOverride = accessScope.detectedMode === "devices"
    ? "devices"
    : accessScope.platform === "edu"
      ? "edu"
      : "";
  const workspaceLoadingProps = isWorkspaceRoute
    ? {
        iconFallbackOverride: workspaceLogo,
        iconOverride: workspaceLogo,
        labelOverride: "Workspace",
        primaryColorOverride: "#868978",
      }
    : {};
  const profileLoadingProps = isProfileRoute
    ? {
        iconFallbackOverride: PROFILE_ICON,
        iconOverride: profileImageUrl || PROFILE_ICON,
        labelOverride: "Profile",
        primaryColorOverride: PROFILE_COLOR,
      }
    : {};
  const showSessionLoading = useMinimumLoading(loading || !profileReady);
  const showAccessLoading = useMinimumLoading(!checked && !checkedScopeKey);

  useEffect(() => {
    if (
      path &&
      path !== "/login" &&
      path !== "/signup" &&
      path !== "/join" &&
      path !== "/forgot-password" &&
      path !== "/reset-password" &&
      path !== "/pending-approval" &&
      !path.startsWith("/no-access")
    ) {
      sessionStorage.setItem("lastPath", path);
    }
  }, [path]);

  console.log("RequireAuth Render", {
    user,
    profile,
    profileReady,
    loading,
    checked,
    hasAccess,
    path,
  });

  // =========================
  // ACCESS CHECK
  // =========================
  useEffect(() => {
    if (loading || !userId || !profileReady) {
      console.log("Waiting for auth readiness", {
        loading,
        userId,
        profileReady,
      });
      return;
    }

    if (profile && profile.is_approved === false) {
      console.log("RequireAuth:pending-detected-from-profile", {
        userId: user.id,
        profile,
        path,
      });
      setHasAccess(false);
      setChecked(true);
      return;
    }

    if (checked && checkedScopeKey === accessCheckKey && !(isEduLauncherRoute && hasAccess === false)) {
      return;
    }

    const checkAccess = async () => {
      console.log("Access Check Start");

      try {
        const { detectedMode, platform, mode } = accessScope;

        console.log("Access Query Params", {
          path,
          detectedMode,
          platform,
          mode,
          user: userId,
        });

        if (isEduLauncherRoute) {
          const orgAccess = await fetchOrganizationAccess(userId, "edu");
          const membershipStatus = String(orgAccess?.membership?.status || "").toLowerCase();
          const cachedEduOrganization = getCachedEduOrganization();
          const hasEduLauncherAccess =
            Boolean(orgAccess?.account?.id) &&
            (orgAccess?.isOwner === true || membershipStatus === "active");
          const hasSelectedEduOrganization = Boolean(cachedEduOrganization?.id);

          console.log("RequireAuth:edu-launcher-org-access", {
            userId,
            orgAccess,
            cachedEduOrganization,
            hasEduLauncherAccess,
            hasSelectedEduOrganization,
          });

          setHasAccess(hasEduLauncherAccess || hasSelectedEduOrganization);
          return;
        }

        const start = Date.now();

        const { data, error } = await supabase
          .from("user_access")
          .select("*")
          .eq("user_id", userId)
          .eq("platform", platform)
          .eq("mode", mode)
          .limit(1);

        const end = Date.now();

        console.log("Query Duration (ms)", end - start);
        console.log("Access Query Result", data, error);

        if (error) {
          setHasAccess(false);
        } else if (!data || data.length === 0) {
          if (platform === "campus" && mode === "teacher_portal") {
            try {
              const orgAccess = await fetchOrganizationAccess(userId, platform);
              const isOwner = orgAccess?.isOwner === true;

              console.log("RequireAuth:campus-teacher-owner-fallback", {
                userId,
                orgAccess,
                isOwner,
              });

              setHasAccess(isOwner);
            } catch (orgAccessError) {
              console.error("Campus teacher portal owner fallback error:", orgAccessError);
              setHasAccess(false);
            }
          } else if ((platform === "campus" || platform === "edu") && mode === "default") {
            try {
              const orgAccess = await fetchOrganizationAccess(userId, platform);
              const isEduAdminRoute =
                normalizedPath.startsWith("/devices/admin") ||
                normalizedPath.startsWith("/edu/admin") ||
                normalizedPath.startsWith("/workspace/admin") ||
                normalizedPath.startsWith("/classroom/admin");
              const orgRole = String(orgAccess?.membership?.role || "").toLowerCase();
              const hasCampusOrgAccess =
                Boolean(orgAccess?.account?.id) &&
                (orgAccess?.isOwner === true ||
                  (
                    String(orgAccess?.membership?.status || "").toLowerCase() === "active" &&
                    (!isEduAdminRoute || ["owner", "admin"].includes(orgRole))
                  ));

              console.log("RequireAuth:campus-org-fallback", {
                userId,
                orgAccess,
                hasCampusOrgAccess,
              });

              setHasAccess(hasCampusOrgAccess);
            } catch (orgAccessError) {
              console.error("Campus org access fallback error:", orgAccessError);
              setHasAccess(false);
            }
          } else {
            setHasAccess(false);
          }
        } else {
          setHasAccess(Boolean(data[0].has_access));
        }

      } catch (err) {
        console.log("Access Crash", err);
        setHasAccess(false);
      } finally {
        console.log("Access Check Complete");
        setChecked(true);
        setCheckedScopeKey(accessCheckKey);
      }
    };

    if (!checkedScopeKey) {
      setChecked(false);
    }
    checkAccess();

  }, [
    userId,
    profile?.is_approved,
    profileReady,
    loading,
    accessScope,
    accessCheckKey,
    checked,
    checkedScopeKey,
    hasAccess,
    path,
    isEduLauncherRoute,
  ]);

  // =========================
  // RENDER STATES
  // =========================

  if (showSessionLoading) {
    console.log("Render State: loading");
    return (
      <GlobalLoadingPage
        title={isWorkspaceRoute ? "Loading Workspace" : isProfileRoute ? "Loading Profile" : "Loading"}
        detail={isWorkspaceRoute ? "Restoring your Workspace session." : isProfileRoute ? "Opening your profile." : "Restoring your session, profile, and login state..."}
        modeOverride={loadingModeOverride}
        {...workspaceLoadingProps}
        {...profileLoadingProps}
      />
    );
  }

  if (!user) {
    console.log("Render State: no user");
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: path }}
      />
    );
  }

  if (profile && profile.is_approved === false && path !== "/pending-approval") {
    console.log("Render State: pending approval");
    console.log("RequireAuth:redirect-pending-approval", {
      userId: user.id,
      profile,
      path,
    });
    return (
      <Navigate
        to="/pending-approval"
        replace
        state={{ from: path }}
      />
    );
  }

  if (showAccessLoading) {
    console.log("Render State: checking access");
    return (
      <GlobalLoadingPage
        title={isWorkspaceRoute ? "Loading Workspace" : isProfileRoute ? "Loading Profile" : "Checking Access"}
        detail={isWorkspaceRoute ? "Checking your Workspace access." : isProfileRoute ? "Checking your profile access." : "Verifying your permissions for this part of Oikos..."}
        modeOverride={loadingModeOverride}
        {...workspaceLoadingProps}
        {...profileLoadingProps}
      />
    );
  }

  if (hasAccess === false) {
    console.log("Render State: no access");
    return (
      <Navigate
        to="/no-access"
        replace
        state={{ from: path }}
      />
    );
  }

  console.log("Render State: access granted");

  return children;
}
